import { ComponentType } from 'react';

import type { IconProps } from '@components/icons';
import { IconBrandAnthropic } from '@components/icons/brand-anthropic';
import { IconBrandOpenAI } from '@components/icons/brand-openai';
import { getScopedTranslate } from '@locale/translate';

import type { AttributeFieldType } from 'api/attributes/requests';
import { AIModel, AIReasoningEffort } from 'api/types';

export const PROVIDER_ORDER = ['OpenAI', 'Anthropic'] as const;

export type AgentOutputProperty = {
  name: string;
  label: string;
  type: AttributeFieldType;
  instructions: string;
  options?: { value: string }[];
};

export const DEFAULT_AGENT_OUTPUT: AgentOutputProperty = {
  name: 'response',
  label: 'Response',
  type: 'paragraph',
  instructions: 'The response to the agent request',
};

export type ModelProvider = (typeof PROVIDER_ORDER)[number];

export type ModelDefinition = {
  value: AIModel;
  labelKey: keyof typeof locale.en;
  helpTextKey: keyof typeof locale.en;
  provider: ModelProvider;
  reasoningEfforts: AIReasoningEffort[];
  defaultReasoningEffort: AIReasoningEffort;
};

export const MODEL_DEFINITIONS: ModelDefinition[] = [
  {
    value: 'claude-opus-4-6',
    labelKey: 'claudeOpus',
    helpTextKey: 'claudeOpusHelpText',
    provider: 'Anthropic',
    reasoningEfforts: ['low', 'medium', 'high', 'max'],
    defaultReasoningEffort: 'high',
  },
  {
    value: 'claude-sonnet-4-6',
    labelKey: 'claudeSonnet',
    helpTextKey: 'claudeSonnetHelpText',
    provider: 'Anthropic',
    reasoningEfforts: ['low', 'medium', 'high'],
    defaultReasoningEffort: 'high',
  },
  {
    value: 'claude-haiku-4-5',
    labelKey: 'claudeHaiku',
    helpTextKey: 'claudeHaikuHelpText',
    provider: 'Anthropic',
    reasoningEfforts: [],
    defaultReasoningEffort: 'low',
  },
  {
    value: 'gpt-5.4',
    labelKey: 'gpt54',
    helpTextKey: 'gpt54HelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoningEffort: 'none',
  },
  {
    value: 'gpt-5.4-mini',
    labelKey: 'gpt54Mini',
    helpTextKey: 'gpt54MiniHelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoningEffort: 'none',
  },
  {
    value: 'gpt-5.4-nano',
    labelKey: 'gpt54Nano',
    helpTextKey: 'gpt54NanoHelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoningEffort: 'none',
  },
  {
    value: 'gpt-5.2',
    labelKey: 'gpt52',
    helpTextKey: 'gpt52HelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoningEffort: 'none',
  },
  {
    value: 'gpt-5.1',
    labelKey: 'gpt51',
    helpTextKey: 'gpt51HelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['none', 'low', 'medium', 'high'],
    defaultReasoningEffort: 'none',
  },
  {
    value: 'gpt-5',
    labelKey: 'gpt5',
    helpTextKey: 'gpt5HelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['minimal', 'low', 'medium', 'high'],
    defaultReasoningEffort: 'medium',
  },
  {
    value: 'gpt-5-mini',
    labelKey: 'gpt5Mini',
    helpTextKey: 'gpt5MiniHelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['minimal', 'low', 'medium', 'high'],
    defaultReasoningEffort: 'medium',
  },
  {
    value: 'gpt-5-nano',
    labelKey: 'gpt5Nano',
    helpTextKey: 'gpt5NanoHelpText',
    provider: 'OpenAI',
    reasoningEfforts: ['minimal', 'low', 'medium', 'high'],
    defaultReasoningEffort: 'medium',
  },
];

const MODEL_MAP = new Map(MODEL_DEFINITIONS.map((modelDef) => [modelDef.value, modelDef]));

const REASONING_EFFORT_LOCALE_MAP: Record<AIReasoningEffort, keyof typeof locale.en> = {
  none: 'reasoningNone',
  minimal: 'reasoningMinimal',
  low: 'reasoningLow',
  medium: 'reasoningMedium',
  high: 'reasoningHigh',
  xhigh: 'reasoningExtraHigh',
  max: 'reasoningMax',
};

const getReasoningEffortLabel = (reasoningEffort: AIReasoningEffort): string => {
  const { t } = getScopedTranslate(locale);
  const key = REASONING_EFFORT_LOCALE_MAP[reasoningEffort];
  if (key) return t(key);
  return reasoningEffort;
};

export const getModelProviderName = (model: AIModel): ModelProvider => {
  return MODEL_MAP.get(model)?.provider ?? 'OpenAI';
};

export const getModelLabel = (model: AIModel): string => {
  const modelDef = MODEL_MAP.get(model);
  if (!modelDef) return model;
  const { t } = getScopedTranslate(locale);
  return t(modelDef.labelKey);
};

export const getModelHelpText = (model: AIModel): string | undefined => {
  const modelDef = MODEL_MAP.get(model);
  if (!modelDef) return undefined;
  const { t } = getScopedTranslate(locale);
  return t(modelDef.helpTextKey);
};

export const getReasoningEffortConfig = (
  model: AIModel
): { options: { value: string; label: string }[]; default: string } | undefined => {
  const modelDef = MODEL_MAP.get(model);
  if (!modelDef || modelDef.reasoningEfforts.length === 0) return undefined;

  return {
    options: modelDef.reasoningEfforts.map((reasoningEffort) => {
      return {
        value: reasoningEffort,
        label: getReasoningEffortLabel(reasoningEffort),
      };
    }),
    default: modelDef.defaultReasoningEffort,
  };
};

export const normalizeReasoningEffortForModel = (
  model: AIModel,
  reasoningEffort?: AIReasoningEffort | null
): AIReasoningEffort | undefined => {
  const config = getReasoningEffortConfig(model);
  if (!config) return undefined;
  if (
    reasoningEffort &&
    config.options.some((option) => {
      return option.value === reasoningEffort;
    })
  ) {
    return reasoningEffort;
  }
  return config.default as AIReasoningEffort;
};

export const getVisibleModels = (hiddenModels: string[]): ModelDefinition[] => {
  const hiddenSet = new Set(hiddenModels);
  return MODEL_DEFINITIONS.filter((modelDef) => !hiddenSet.has(modelDef.value));
};

export const PROVIDER_ICONS: Record<ModelProvider, ComponentType<IconProps>> = {
  Anthropic: IconBrandAnthropic,
  OpenAI: IconBrandOpenAI,
};

export const DEFAULT_MODEL: AIModel = 'gpt-5.4';

export const locale = {
  en: {
    claudeOpus: 'Claude Opus 4.6',
    claudeOpusHelpText: 'Most capable Anthropic model for complex analysis and generation',
    claudeSonnet: 'Claude Sonnet 4.6',
    claudeSonnetHelpText: 'Balanced Anthropic model for everyday tasks with strong reasoning',
    claudeHaiku: 'Claude Haiku 4.5',
    claudeHaikuHelpText: 'Fast and lightweight Anthropic model for simple, high-throughput tasks',
    gpt54: 'GPT-5.4',
    gpt54HelpText: 'Most capable and efficient frontier model for professional work',
    gpt54Mini: 'GPT-5.4 mini',
    gpt54MiniHelpText: 'Strongest mini model for coding, computer use, and sub-agents',
    gpt54Nano: 'GPT-5.4 nano',
    gpt54NanoHelpText: 'Cheapest GPT-5.4-class model for simple, high-volume tasks',
    gpt52: 'GPT-5.2',
    gpt52HelpText: 'Previous generation GPT-5 model for complex tasks and high-quality outputs',
    gpt51: 'GPT-5.1',
    gpt51HelpText: 'Earlier GPT-5 generation with strong general-purpose performance',
    gpt5: 'GPT-5',
    gpt5HelpText: 'Balanced GPT-5 model for everyday coding and agent workflows',
    gpt5Mini: 'GPT 5 mini',
    gpt5MiniHelpText: 'Legacy smaller GPT-5 model optimized for speed and lower cost',
    gpt5Nano: 'GPT 5 nano',
    gpt5NanoHelpText: 'Legacy lightweight GPT-5 model for simple, high-throughput tasks',
    reasoningNone: 'None',
    reasoningLow: 'Low',
    reasoningMinimal: 'Minimal',
    reasoningMedium: 'Medium',
    reasoningHigh: 'High',
    reasoningExtraHigh: 'Extra High',
    reasoningMax: 'Max',
  },
};
